import { useState, isValidElement, Children } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const ACCENT_SLIDER = {
  orange: 'accent-orange-500',
  blue: 'accent-blue-600',
  purple: 'accent-purple-500',
  amber: 'accent-amber-500',
  gray: 'accent-gray-500',
};

const ACCENT_DOT = {
  orange: 'bg-orange-500',
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  amber: 'bg-amber-500',
  gray: 'bg-gray-400',
};

/** Counts how many of `keys` differ between `current` and `defaults` — powers the "N modified" badge. */
export function countModified(current, defaults, keys) {
  return keys.filter(k => JSON.stringify(current?.[k]) !== JSON.stringify(defaults?.[k])).length;
}

/** Recursively walks a React element tree collecting searchable text (titles, row labels) without rendering it. */
function collectSearchText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (Array.isArray(node)) return node.map(collectSearchText).join(' ');
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!isValidElement(node)) return '';
  const props = node.props || {};
  let text = '';
  if (typeof props.title === 'string') text += ' ' + props.title;
  if (typeof props.label === 'string') text += ' ' + props.label;
  if (typeof props['data-search-label'] === 'string') text += ' ' + props['data-search-label'];
  if (props.children) text += ' ' + collectSearchText(props.children);
  return text;
}

function textMatches(text, query) {
  return text.toLowerCase().includes(query);
}

/** Search box for jumping to a setting by name across all groups in a tab. */
export function SettingsSearchBar({ value, onChange, placeholder = 'Search settings…' }) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm border rounded-lg pl-8 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Outer collapsible section (one of the top-level cards on the Advanced tab). Open/closed state is
 * fully user-controlled via `open`/`onToggle` — searching never forces it open unless something
 * inside it actually matches, and it disappears entirely (header included) when nothing matches.
 */
export function SettingsCard({ title, description, icon: Icon, open, onToggle, searchActive = false, query = '', children }) {
  const q = query.trim().toLowerCase();
  const visible = !searchActive || textMatches(title + ' ' + (description || '') + ' ' + collectSearchText(children), q);

  if (!visible) return null;
  const isOpen = searchActive ? true : open;

  return (
    <div className="card">
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5" />}
            {title}
          </h3>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
        <ChevronDownIcon className={clsx('w-5 h-5 text-gray-400 transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="p-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Named, collapsible sub-section of related settings. Collapsed by default so a category
 * with 40+ controls reads as a table of contents, not one continuous scroll.
 * While searching, only children whose label matches survive — everything else (including this
 * group's own header, when nothing inside it matches) is hidden until the search is cleared.
 */
export function SettingsGroup({ title, badge = 0, accent = 'gray', defaultOpen = false, searchActive = false, query = '', children }) {
  const [open, setOpen] = useState(defaultOpen);
  const q = query.trim().toLowerCase();

  let visibleChildren = Children.toArray(children);
  let hasMatch = true;

  if (searchActive) {
    const titleMatches = textMatches(title, q);
    if (!titleMatches) {
      visibleChildren = visibleChildren.filter(child => textMatches(collectSearchText(child), q));
    }
    hasMatch = titleMatches || visibleChildren.length > 0;
  }

  if (searchActive && !hasMatch) return null;
  const isOpen = searchActive ? true : open;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', ACCENT_DOT[accent])} />
          <span className="text-sm font-semibold text-gray-800 truncate">{title}</span>
          {badge > 0 && (
            <span className="text-[10px] leading-none font-medium bg-amber-100 text-amber-700 px-1.5 py-1 rounded-full flex-shrink-0">
              {badge} modified
            </span>
          )}
        </span>
        <ChevronDownIcon className={clsx('w-4 h-4 text-gray-400 transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="px-3 py-3 space-y-4 bg-gray-50/60 border-t border-gray-200">
          {visibleChildren}
        </div>
      )}
    </div>
  );
}

/** Compact slider row: label + value on one line, slider below, description tucked behind an info icon. */
export function SliderRow({ label, value, defaultValue, onChange, min, max, step, format, minLabel, maxLabel, description, accent = 'gray' }) {
  const isModified = defaultValue !== undefined && String(value) !== String(defaultValue);
  const display = format ? format(value) : value;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="flex items-center gap-1.5 min-w-0">
          <label className="text-sm text-gray-700 truncate">{label}</label>
          {isModified && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Modified from default" />}
        </span>
        <span className="text-xs font-mono text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap">{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className={clsx('w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer', ACCENT_SLIDER[accent])}
      />
      {(minLabel || maxLabel) && (
        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>{minLabel}</span><span>{maxLabel}</span></div>
      )}
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );
}

/** Compact checkbox row with its description always visible underneath. */
export function ToggleRow({ label, checked, onChange, description, accent = 'gray' }) {
  return (
    <div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onChange} className={clsx('w-4 h-4 rounded flex-shrink-0', ACCENT_SLIDER[accent])} />
        <span className="text-sm text-gray-700">{label}</span>
      </label>
      {description && (
        <p className="text-xs text-gray-500 mt-1 ml-6">{description}</p>
      )}
    </div>
  );
}
